import axiosClient from '../axiosClient';
import type { AxiosRequestConfig } from 'axios';
import type {
  DataModelResponse,
  CreateDataModelRequest,
  UpdateDataModelRequest,
  BaseQueryPreviewResponse,
  AggregateFunctionOption
} from '@/api/contracts/dataModel';
import { assertGuidString } from '@/utils/guid';

export interface DataModelDeleteDryRunDataViewItem {
  viewId: string;
  name: string;
  isDefault: boolean;
}

export interface DataModelDeleteDryRunResponse {
  modelId: string;
  name: string;
  dataViews: DataModelDeleteDryRunDataViewItem[];
}

/**
 * Bug 2843: henter den backend-eide aggregat-funksjons-katalogen (value + label). Modell-editorens
 * relasjons-dialog rendrer denne i stedet for et hardkodet funksjons-vokabular — samme mønster som
 * `fetchDataViewOperators`. Verdiene er camelCase (matcher en relasjons persisterte `function`).
 */
export async function fetchAggregateFunctions (signal?: AbortSignal): Promise<AggregateFunctionOption[]> {
  const response = await axiosClient.get<AggregateFunctionOption[]>('data-models/aggregate-functions', { signal });
  return response.data;
}

// Wire-nøkkelen for datamodellens tabell-alias er uendret `tableId` (+ from/to/main),
// bevart av BE via [JsonPropertyName]. Lokalt heter symbolet `alias` (from/to/mainAlias).
// Disse normalize-funksjonene er det sentrale wire↔lokal-mapping-punktet for ALL
// datamodell-I/O; ingen ?? -fallback-kjeder på kontrakt-keys.

function normalizeDataModelResponse (model: DataModelResponse): DataModelResponse {
  const legacyAliasKey = ['table', 'PublicId'].join('');
  const raw = model as unknown as Record<string, unknown>;
  return {
    ...model,
    mainAlias: (model.mainAlias ?? (raw.mainTableId as string | null | undefined)) ?? null,
    tables: (model.tables ?? []).map((t) => {
      const table = t as unknown as Record<string, unknown>;
      const tableKey = String(table.tableKey || table[legacyAliasKey] || '').trim();
      return {
        ...t,
        alias: String(t.alias ?? table.tableId ?? '').trim(),
        tableKey,
        columns: t.columns ?? []
      };
    }),
    relations: (model.relations ?? []).map((r) => {
      const rel = r as unknown as Record<string, unknown>;
      return {
        ...r,
        fromAlias: String(r.fromAlias ?? rel.fromTableId ?? '').trim(),
        toAlias: String(r.toAlias ?? rel.toTableId ?? '').trim()
      };
    })
  };
}

function normalizeDataModelRequest<T extends CreateDataModelRequest | UpdateDataModelRequest> (request: T): T {
  const legacyAliasKey = ['table', 'PublicId'].join('');
  const normalizedTables = (request.tables ?? []).map((table) => {
    const raw = table as unknown as Record<string, unknown>;
    const tableKey = String(raw.tableKey || raw[legacyAliasKey] || '').trim();
    const normalizedAlias = String(raw.alias ?? '').trim();
    const normalizedDisplayName = String(raw.displayName ?? '').trim();
    if (!normalizedAlias) {
      throw new Error('Tabell mangler alias');
    }

    return {
      tableId: normalizedAlias,
      tableKey,
      displayName: normalizedDisplayName,
      columns: table.columns ?? [],
      canvasX: table.canvasX,
      canvasY: table.canvasY,
    };
  });

  const normalizedRelations = (request.relations ?? []).map((relation) => {
    const raw = relation as unknown as Record<string, unknown>;
    const fromTableId = String(raw.fromAlias ?? '').trim();
    const toTableId = String(raw.toAlias ?? '').trim();
    if (!fromTableId) {
      throw new Error('Relasjon mangler fra-tabell');
    }
    if (!toTableId) {
      throw new Error('Relasjon mangler til-tabell');
    }
    const aggregates = relation.aggregates;
    return {
      fromTableId,
      fromColumnId: relation.fromColumnId,
      toTableId,
      toColumnId: relation.toColumnId,
      cardinality: relation.cardinality,
      // Aggregat-JOIN: per-kolonne funksjon (count/sum/string_agg). Wire-nøkkelen er
      // `aggregates` på begge sider; må mappes eksplisitt her siden vi bygger et nytt
      // objekt (ikke spread). Utelates når tom = vanlig relasjon.
      ...(aggregates && aggregates.length > 0 ? { aggregates } : {})
    };
  });

  const { mainAlias, ...rest } = request;
  return {
    ...rest,
    mainTableId: mainAlias ?? null,
    tables: normalizedTables,
    relations: normalizedRelations
  } as unknown as T;
}

export async function getDataModels (signal?: AbortSignal): Promise<DataModelResponse[]> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataModelResponse[]>('data-models', config);
  return (response.data ?? []).map(normalizeDataModelResponse);
}

export async function getDataModelById (
  id: string,
  signal?: AbortSignal
): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataModelResponse>(`data-models/${assertGuidString(id, 'modelId')}`, config);
  return normalizeDataModelResponse(response.data);
}

export async function createDataModel (
  request: CreateDataModelRequest,
  signal?: AbortSignal
): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const normalizedRequest = normalizeDataModelRequest(request);
  const response = await axiosClient.post<DataModelResponse>('data-models', normalizedRequest, config);
  return normalizeDataModelResponse(response.data);
}

export async function updateDataModel (
  id: string,
  request: UpdateDataModelRequest,
  signal?: AbortSignal
): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const normalizedRequest = normalizeDataModelRequest(request);
  const response = await axiosClient.put<DataModelResponse>(`data-models/${assertGuidString(id, 'modelId')}`, normalizedRequest, config);
  return normalizeDataModelResponse(response.data);
}

export async function deleteDataModel (id: string, signal?: AbortSignal): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  await axiosClient.delete(`data-models/${assertGuidString(id, 'modelId')}`, config);
}

export async function getDataModelDeleteDryRun (
  id: string,
  signal?: AbortSignal
): Promise<DataModelDeleteDryRunResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataModelDeleteDryRunResponse>(`data-models/${assertGuidString(id, 'modelId')}/delete-dry-run`, config);
  return response.data;
}

export async function getDataModelBaseQueryPreview (
  id: string,
  signal?: AbortSignal
): Promise<BaseQueryPreviewResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<BaseQueryPreviewResponse>(`data-models/${assertGuidString(id, 'modelId')}/base-query-preview`, config);
  return response.data;
}

export async function fixDataModel (id: string, signal?: AbortSignal): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.post<DataModelResponse>(`data-models/${assertGuidString(id, 'modelId')}/fix`, null, config);
  return normalizeDataModelResponse(response.data);
}

export async function regenerateDataModelMaterialization (id: string, signal?: AbortSignal): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.post<DataModelResponse>(`data-models/${assertGuidString(id, 'modelId')}/regenerate-materialization`, null, config);
  return normalizeDataModelResponse(response.data);
}
